import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Mail, Loader2, Info } from "lucide-react";
import { dialogContentOutsideGuards } from "../../utils/dialogOutsideGuards";

/**
 * Dialog for emailing a technician's PDF report.
 * Submits to the parent's `onSubmit({ toEmail, subject, bodyHtml })`.
 *
 * Subject / body are optional in the payload — if left blank the backend
 * falls back to either business_settings.report_email_template or the
 * built-in default. Showing a placeholder hint lets users edit when they
 * want, but the most-common "just send it" path requires zero typing.
 */
const EmailReportDialog = ({
  open,
  onOpenChange,
  technicianName,
  technicianEmail,
  periodLabel,
  onSubmit,
}) => {
  const [toEmail, setToEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    setToEmail(technicianEmail || "");
    setSubject("");
    setBody("");
  }, [open, technicianEmail]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!toEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim())) {
      toast.error("Please enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const result = await onSubmit({
        toEmail: toEmail.trim(),
        subject: subject.trim() || undefined,
        bodyHtml: body.trim() || undefined,
      });
      if (result?.success === false) {
        toast.error(result.error || "Failed to send email");
      } else {
        toast.success(`Report sent to ${toEmail.trim()}`);
        onOpenChange?.(false);
      }
    } catch (err) {
      toast.error(err?.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        data-testid="email-tech-report-dialog"
        {...dialogContentOutsideGuards}
      >
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-500" />
            Email PDF report
          </DialogTitle>
          <DialogDescription className="text-xs">
            Send {technicianName ? `${technicianName}'s` : "the technician's"} performance
            report for <strong>{periodLabel}</strong> as a PDF attachment.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <Label htmlFor="email-to">To</Label>
            <Input
              id="email-to"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="recipient@example.com"
              required
              data-testid="email-to-input"
            />
            {!technicianEmail && (
              <p className="text-[11px] text-amber-600 mt-1">
                This technician has no email on file — please enter one.
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="email-subject" className="flex items-center gap-1.5">
              Subject <span className="text-[10px] text-muted-foreground/60">(optional)</span>
            </Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Your performance report — ${periodLabel}`}
              data-testid="email-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="email-body" className="flex items-center gap-1.5">
              Message <span className="text-[10px] text-muted-foreground/60">(optional)</span>
            </Label>
            <Textarea
              id="email-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder={`Hi ${technicianName || "[name]"}, please find your performance report for ${periodLabel} attached.`}
              data-testid="email-body-input"
            />
            <p className="text-[11px] text-muted-foreground/60 mt-1 flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              Leave blank to use the default template. You can save a custom
              template under Settings → Business → Email Template so you
              don&apos;t need to retype it each time.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sending}
              data-testid="email-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="email-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send email
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmailReportDialog;
